'use client'

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import {
  JobBoardVolunteer,
  JobBoardTeam,
  createJobBoardTeam,
  updateJobBoardTeam,
} from '../../api/work-orders.api'
import { Users, Search, Check, Plus, Star, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'

interface ManageTeamRosterModalProps {
  isOpen: boolean
  onClose: () => void
  team:
    | JobBoardTeam
    | {
        id?: number
        teamLeadId: number | null
        teamName: string
        volunteerIds: number[]
      }
  weekStart: string
  regionId: number
  allVolunteers: JobBoardVolunteer[]
  onSaved: () => void
}

export function ManageTeamRosterModal({
  isOpen,
  onClose,
  team,
  weekStart,
  regionId,
  allVolunteers,
  onSaved,
}: ManageTeamRosterModalProps) {
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [teamName, setTeamName] = useState('')
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen && team) {
      setSelectedIds(Array.isArray(team.volunteerIds) ? [...team.volunteerIds] : [])
      setTeamName(team.teamName || '')
      setSelectedLeadId(team.teamLeadId ?? null)
      setSearch('')
    }
  }, [isOpen, team])

  const toggleVolunteer = (userId: number) => {
    if (selectedIds.includes(userId)) {
      setSelectedIds(selectedIds.filter((id) => id !== userId))
    } else {
      setSelectedIds([...selectedIds, userId])
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const finalName = teamName.trim() || team.teamName || 'Team'
      if (team.id) {
        const res = await updateJobBoardTeam(team.id, {
          teamName: finalName,
          teamLeadId: selectedLeadId,
          volunteerIds: selectedIds,
        })
        if (res.success) {
          toast.success(`Team "${finalName}" updated`)
          onSaved()
          onClose()
        } else {
          toast.error(res.message || 'Failed to update team')
        }
      } else {
        const res = await createJobBoardTeam({
          regionId,
          weekStart,
          teamLeadId: selectedLeadId ?? undefined,
          teamName: finalName,
          volunteerIds: selectedIds,
        })
        if (res.success) {
          toast.success(`Team "${finalName}" created`)
          onSaved()
          onClose()
        } else {
          toast.error(res.message || 'Failed to save team')
        }
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error updating team')
    } finally {
      setIsSaving(false)
    }
  }

  const filteredVolunteers = allVolunteers.filter((vol) => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    return (
      vol.fullName.toLowerCase().includes(q) ||
      (vol.groupName && vol.groupName.toLowerCase().includes(q)) ||
      (vol.role && vol.role.toLowerCase().includes(q))
    )
  })

  // Sort: assigned to this team first, then alphabetical
  filteredVolunteers.sort((a, b) => {
    const aIn = selectedIds.includes(a.userId)
    const bIn = selectedIds.includes(b.userId)
    if (aIn && !bIn) return -1
    if (!aIn && bIn) return 1
    return a.fullName.localeCompare(b.fullName)
  })

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                <Users className="w-4 h-4" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold">
                  {team.id ? 'Edit Team & Crew' : 'Create Team & Crew'}
                </DialogTitle>
                <div className="text-xs text-muted-foreground">
                  Set team name, optional leader, and crew roster. All team work orders receive this crew.
                </div>
              </div>
            </div>
            <Badge variant="secondary" className="text-xs px-2 py-0.5">
              {selectedIds.length} Volunteers
            </Badge>
          </div>

          {/* Team Settings Inputs: Team Name & Team Leader */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 pt-3 border-t">
            <div>
              <label className="text-[11px] font-semibold text-foreground mb-1 block">
                Team Name
              </label>
              <Input
                value={teamName}
                onChange={(e) => setTeamName((e.target as HTMLInputElement).value)}
                placeholder="e.g. Team Alpha, Roofing Crew A..."
                className="h-8 text-xs bg-background"
              />
            </div>

            <div>
              <label className="text-[11px] font-semibold text-foreground mb-1 block">
                Team Leader (Optional)
              </label>
              <select
                value={selectedLeadId ? String(selectedLeadId) : ''}
                onChange={(e) =>
                  setSelectedLeadId(e.target.value ? Number(e.target.value) : null)
                }
                className="w-full h-8 text-xs px-2 rounded-md border border-input bg-background font-medium focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">No Leader Assigned Yet</option>
                {(() => {
                  const isLead = (v: any) =>
                    v.experienceLeadingTeams >= 3 ||
                    v.role === 'Team Leader' ||
                    v.role === 'Base Leader' ||
                    v.role === 'Admin'
                  const candidates = allVolunteers.filter(isLead)
                  const others = allVolunteers.filter((v) => !isLead(v))

                  return (
                    <>
                      {candidates.length > 0 && (
                        <optgroup label="Candidate Leaders">
                          {candidates.map((v) => (
                            <option key={v.userId} value={String(v.userId)}>
                              {v.fullName}{' '}
                              {v.experienceLeadingTeams >= 3
                                ? `(Lead ${v.experienceLeadingTeams}/5)`
                                : `(${v.role || 'Leader'})`}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {others.length > 0 && (
                        <optgroup label="Other Volunteers">
                          {others.map((v) => (
                            <option key={v.userId} value={String(v.userId)}>
                              {v.fullName}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  )
                })()}
              </select>
            </div>
          </div>
        </DialogHeader>

        {/* Search */}
        <div className="p-3 border-b bg-background">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              placeholder="Search volunteers by name, group..."
              className="h-8 pl-8 text-xs"
            />
          </div>
        </div>

        {/* Volunteers List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {filteredVolunteers.length === 0 ? (
            <div className="text-center py-12 text-xs text-muted-foreground">
              No on-base volunteers available for this week.
            </div>
          ) : (
            filteredVolunteers.map((vol) => {
              const isSelected = selectedIds.includes(vol.userId)
              const otherTeam =
                vol.teamId && vol.teamId !== team.id ? vol.teamName : null

              return (
                <div
                  key={vol.userId}
                  onClick={() => toggleVolunteer(vol.userId)}
                  className={cn(
                    'p-2.5 rounded-lg border text-xs flex items-center justify-between gap-3 cursor-pointer transition-all select-none',
                    isSelected
                      ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-xs'
                      : 'hover:border-muted-foreground/30 bg-background'
                  )}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div
                      className={cn(
                        'w-5 h-5 rounded flex items-center justify-center border transition-all shrink-0',
                        isSelected
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-input bg-background'
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-foreground truncate">
                          {vol.fullName}
                        </span>
                        {vol.experienceLeadingTeams >= 3 && (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1 py-0 bg-amber-50 text-amber-700 dark:bg-amber-950 border-amber-300 gap-0.5"
                          >
                            <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                            Lead {vol.experienceLeadingTeams}/5
                          </Badge>
                        )}
                        {otherTeam && !isSelected && (
                          <Badge
                            variant="secondary"
                            className="text-[9px] px-1 py-0 bg-muted text-muted-foreground"
                          >
                            On {otherTeam}
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {vol.groupName || 'Visiting Group Volunteer'}
                        {vol.role && ` • ${vol.role}`}
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    variant={isSelected ? 'default' : 'outline'}
                    className={cn(
                      'h-6 text-[11px] px-2 shrink-0 gap-1 font-medium',
                      isSelected ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleVolunteer(vol.userId)
                    }}
                  >
                    {isSelected ? 'Assigned' : '+ Add'}
                  </Button>
                </div>
              )
            })
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="p-3 border-t bg-muted/20 flex items-center justify-between sm:justify-between">
          <div className="text-xs text-muted-foreground">
            <strong>{selectedIds.length}</strong> volunteers selected
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving} className="gap-1.5 font-semibold">
              {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save Team Crew
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
